import { useQuery } from '@tanstack/react-query';
import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { getAccount } from '@/api/auth';
import { hostBaseUrl } from '@/api/config';
import type {
  UseDepositBonusesResult,
  UsePaymentHistoryResult,
  UsePaymentResourcesResult,
  UseSubmitPaymentResult,
} from '@/api/payments';
import { cmsPageQuery } from '@/api/queries';
import {
  PAYMENT_QUICK_AMOUNTS,
  effectiveDepositLimits,
  type PaymentProvider,
} from '@/domain/payments';
import { ULTRAPAY_CRYPTO_PAIRS } from '@/domain/payment-providers';
import { useTranslations } from '@/i18n/TranslationsProvider';
import { cn } from '@/lib/cn';
import { Button, Checkbox, Input, Select, Spinner, Toast } from '@/ui/common';
import { ClaimBonusTermsModal } from '../ClaimBonusTermsModal';
import { PaymentInfoCard } from './PaymentInfoCard';
import { PaymentResult } from './PaymentResult';
import { RecentPaymentRows } from './RecentPaymentRows';
import { SelectedProviderHeader } from './SelectedProviderHeader';

export interface DepositFormPanelProps {
  provider: PaymentProvider;
  currency: string;
  resources: UsePaymentResourcesResult;
  bonuses: UseDepositBonusesResult;
  history: UsePaymentHistoryResult;
  submitPayment: UseSubmitPaymentResult;
  /** Redeposit flow opened from the withdrawals list (legacy DepositRequest). */
  isRedeposit?: boolean;
  changeProviderTo?: '/profile/deposits' | '/profile/withdrawals';
  className?: string;
}

export function DepositFormPanel({
  provider,
  currency,
  resources,
  bonuses,
  history,
  submitPayment,
  isRedeposit = false,
  changeProviderTo = '/profile/deposits',
  className,
}: DepositFormPanelProps) {
  const { t, i18n } = useTranslation();
  const { selectedLanguage } = useTranslations();
  const isTranslationReady =
    selectedLanguage === 'en' || i18n.hasResourceBundle(selectedLanguage, 'translation');
  const label = (key: string, fallback: string) =>
    isTranslationReady ? t(key, { defaultValue: fallback }) : fallback;

  const [amount, setAmount] = useState(String(provider.MinAmount ?? ''));
  const [bonusId, setBonusId] = useState('');
  const [confirmBonus, setConfirmBonus] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [validationError, setValidationError] = useState<string | null>(null);

  const fieldSet = resources.fields;
  const successMode = resources.config?.success ?? 'notify';

  const selectedBonus = useMemo(
    () => bonuses.bonuses.find((bonus) => bonus.Id === bonusId) ?? null,
    [bonuses.bonuses, bonusId],
  );

  const limits = useMemo(
    () => effectiveDepositLimits(provider, selectedBonus),
    [provider, selectedBonus],
  );

  const account = getAccount();
  const accountGuid = account?.Guid?.trim() ?? '';
  const usersBaseUrl = hostBaseUrl('users');
  const termsSlug = selectedBonus?.PageUrl?.trim() || null;
  const termsQuery = useQuery({
    ...cmsPageQuery(accountGuid, termsSlug ?? '', selectedLanguage),
    enabled: Boolean(accountGuid && termsSlug && selectedLanguage && termsOpen),
  });

  const cryptoNetworks = useMemo(() => {
    const coin = fields.cryptocurrency ?? '';
    if (!coin) return [];
    return ULTRAPAY_CRYPTO_PAIRS.filter((pair) => pair.Cryptocurrency === coin).map(
      (pair) => pair.Network,
    );
  }, [fields.cryptocurrency]);

  const cryptoCoins = useMemo(
    () =>
      [...new Set(ULTRAPAY_CRYPTO_PAIRS.map((pair) => pair.Cryptocurrency))],
    [],
  );

  const setField = (key: string, value: string) => {
    setFields((prev) => ({ ...prev, [key]: value }));
  };

  const onBonusChange = (nextId: string) => {
    setBonusId(nextId);
    setConfirmBonus(false);
    setTermsOpen(false);
    setValidationError(null);
  };

  const onConfirmBonusChange = (checked: boolean) => {
    setConfirmBonus(checked);
    if (checked && selectedBonus?.PageUrl?.trim()) {
      setTermsOpen(true);
    }
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setValidationError(null);
    submitPayment.reset();

    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      setValidationError(label('Enter a valid amount.', 'Enter a valid amount.'));
      return;
    }
    if (numericAmount < limits.minAmount) {
      setValidationError(
        `${label('Deposit minimum', 'Deposit minimum')} ${limits.minAmount}`,
      );
      return;
    }
    if (Number.isFinite(limits.maxAmount) && numericAmount > limits.maxAmount) {
      setValidationError(
        `${label('Deposit maximum', 'Deposit maximum')} ${limits.maxAmount}`,
      );
      return;
    }
    if (bonusId && !confirmBonus) {
      setValidationError(
        label('Please confirm the bonus terms.', 'Please confirm the bonus terms.'),
      );
      return;
    }

    try {
      await submitPayment.submit({
        mode: 'deposit',
        provider,
        amount: numericAmount,
        accountBonusId: bonusId || null,
        fields,
      });
      history.refetch();
    } catch (error) {
      setValidationError(
        error instanceof Error ? error.message : label('Payment failed', 'Payment failed'),
      );
    }
  };

  const showBonusSelect = !bonuses.isLoading && bonuses.bonuses.length > 0;
  const eligibilityMessage = !bonuses.isLoading && bonuses.message ? bonuses.message : null;

  return (
    <div className={cn('flex flex-col gap-4 lg:gap-6', className)}>
      <SelectedProviderHeader
        provider={provider}
        changeTo={changeProviderTo}
        selectedLabel={label('Selected provider', 'Selected provider')}
        changeLabel={label('Change provider', 'Change provider')}
      />

      <form onSubmit={onSubmit} className="flex max-w-[560px] flex-col gap-4">
        {fieldSet.includes('bank') && resources.banks.length > 0 ? (
          <Select
            label={label('Bank', 'Bank')}
            options={resources.banks.map((bank) => ({
              value: String(bank.Id),
              label: bank.Name,
            }))}
            placeholder={label('Select bank', 'Select bank')}
            value={fields.bank ?? ''}
            onChange={(event) => setField('bank', event.target.value)}
            required
          />
        ) : null}

        {fieldSet.includes('walletType') && resources.walletTypes.length > 0 ? (
          <Select
            label={label('Wallet type', 'Wallet type')}
            options={resources.walletTypes.map((item) => ({
              value: String(item.Id),
              label: item.Name,
            }))}
            placeholder={label('Select wallet type', 'Select wallet type')}
            value={fields.walletType ?? ''}
            onChange={(event) => setField('walletType', event.target.value)}
            required
          />
        ) : null}

        {fieldSet.includes('cryptocurrency') ? (
          <Select
            label={label('Crypto currency', 'Crypto currency')}
            options={cryptoCoins.map((coin) => ({ value: coin, label: coin }))}
            placeholder={label('Select coin', 'Select coin')}
            value={fields.cryptocurrency ?? ''}
            onChange={(event) => {
              setField('cryptocurrency', event.target.value);
              setField('network', '');
            }}
            required
          />
        ) : null}

        {fieldSet.includes('network') ? (
          <Select
            label={label('Network', 'Network')}
            options={cryptoNetworks.map((network) => ({ value: network, label: network }))}
            placeholder={label('Select network', 'Select network')}
            value={fields.network ?? ''}
            onChange={(event) => setField('network', event.target.value)}
            required
          />
        ) : null}

        {fieldSet.includes('phone') ? (
          <Input
            label={label('Phone', 'Phone')}
            value={fields.phone ?? ''}
            onChange={(event) => setField('phone', event.target.value)}
            required
          />
        ) : null}

        {fieldSet.includes('voucher') ? (
          <Input
            label={label('Voucher PIN', 'Voucher PIN')}
            value={fields.voucher ?? ''}
            onChange={(event) => setField('voucher', event.target.value)}
            required
          />
        ) : null}

        {fieldSet.includes('amount') ? (
          <>
            <Input
              label={label('Amount', 'Amount')}
              inputMode="decimal"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              required
            />
            <div className="flex flex-wrap gap-2">
              {PAYMENT_QUICK_AMOUNTS.map((quick) => (
                <Button
                  key={quick}
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    const current = Number(amount) || 0;
                    setAmount(String(current + quick));
                  }}
                >
                  {`+${quick}`}
                </Button>
              ))}
            </div>
          </>
        ) : null}

        {bonuses.isLoading ? (
          <Spinner size="sm" />
        ) : null}

        {eligibilityMessage ? (
          <Toast tone="danger">
            {label(eligibilityMessage, eligibilityMessage)}
          </Toast>
        ) : null}

        {showBonusSelect ? (
          <div className="flex flex-col gap-2">
            <Select
              label={label('Deposit bonus', 'Deposit bonus')}
              options={[
                { value: '', label: label('NoBonus', 'No bonus') },
                ...bonuses.bonuses.map((bonus) => ({
                  value: bonus.Id,
                  label: bonus.Name ?? bonus.Id,
                })),
              ]}
              value={bonusId}
              onChange={(event) => onBonusChange(event.target.value)}
            />
            {bonusId ? (
              <Checkbox
                checked={confirmBonus}
                onChange={(event) => onConfirmBonusChange(event.target.checked)}
                label={label(
                  'I accept the bonus terms and conditions',
                  'I accept the bonus terms and conditions',
                )}
              />
            ) : null}
          </div>
        ) : null}

        {validationError ? <Toast tone="danger">{validationError}</Toast> : null}

        <PaymentResult
          response={submitPayment.result}
          successMode={successMode}
          successTitle={
            isRedeposit
              ? label('Redeposit submitted', 'Redeposit submitted')
              : label('Deposit submitted', 'Deposit submitted')
          }
          errorTitle={
            isRedeposit
              ? label('Redeposit failed', 'Redeposit failed')
              : label('Deposit failed', 'Deposit failed')
          }
          closeLabel={label('Close', 'Close')}
          copyLabel={label('Copy', 'Copy')}
          copiedLabel={label('Copied', 'Copied')}
          onDismiss={() => submitPayment.reset()}
        />

        <Button type="submit" fullWidth loading={submitPayment.isSubmitting}>
          {isRedeposit ? label('Redeposit', 'Redeposit') : label('Deposit', 'Deposit')}
        </Button>

        <PaymentInfoCard
          provider={provider}
          currency={currency}
          limitsLabel={label('Limits', 'Limits')}
          durationLabel={label('Duration', 'Duration')}
          feeLabel={label('Fee', 'Fee')}
          limitsOverride={
            selectedBonus
              ? {
                  MinAmount: limits.minAmount,
                  MaxAmount: Number.isFinite(limits.maxAmount)
                    ? limits.maxAmount
                    : provider.MaxAmount,
                }
              : null
          }
        />
      </form>

      {history.isLoading ? (
        <div className="flex justify-center py-6">
          <Spinner size="md" />
        </div>
      ) : (
        <RecentPaymentRows
          rows={history.rows}
          currency={currency}
          title={label('Recent deposits', 'Recent deposits')}
          emptyLabel={label('No recent deposits.', 'No recent deposits.')}
          cancelLabel={label('Cancel', 'Cancel')}
          onCancel={(encId) => {
            void history.cancel(encId);
          }}
          cancelling={history.isCancelling}
        />
      )}

      <ClaimBonusTermsModal
        open={termsOpen}
        onClose={() => setTermsOpen(false)}
        page={termsQuery.data ?? null}
        isLoading={termsQuery.isLoading}
        usersBaseUrl={usersBaseUrl}
        accountGuid={accountGuid}
        label={label}
      />
    </div>
  );
}
